<template>
  <div class="main">
    <div class="error-container">
      <div class="error-icon"></div>
      <h1 class="error-title">{{ title }}</h1>
      <p class="error-description">{{ description }}</p>
      <p class="device-id" v-if="deviceTag">
        {{ t("DEVICE_TAG_CL_HELP", { deviceTag }) }}
      </p>
    </div>
    <div class="button-container">
      <slot></slot>
      <button class="try-button" @click="dismiss()">{{ buttonText }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { useRoute, useRouter } from "vue-router";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";

declare const Android: AndroidInterface;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const props = defineProps({ customDismiss: { type: Boolean, default: false } });
const emit = defineEmits(["onDismiss"]);

const deviceTag = ref<string>(AuthenticationHelper.getDeviceTag());

const title =
  route.query?.title?.toString() || t("CREDIT_LINE_NETWORK_ERROR_MESSAGE");

const description =
  route.query?.description?.toString() ||
  t("CREDIT_LINE_NETWORK_ERROR_TRY_AGAIN");

const buttonText = route.query?.buttonText?.toString() || t("TRYAGAIN");

const dismissAndroidWebView = () => {
  if (isAndroid() && typeof Android?.dismissWebView === "function") {
    Android.dismissWebView();
    return true;
  }
  return false;
};

const dismiss = () => {
  if (props.customDismiss) {
    emit("onDismiss");
    return;
  }
  const returnHomeOnTryAgain = route.query?.returnHomeOnTryAgain === "true";

  if (returnHomeOnTryAgain) {
    if (dismissAndroidWebView()) return;
  }

  if (window.history.length > 1) {
    router.back();
    return;
  }

  if (dismissAndroidWebView()) return;
};

onMounted(() => {
  if (isAndroid() && typeof Android?.finishedLoading === "function") {
    Android.finishedLoading();
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";

* {
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
}

.main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 1rem;
  text-align: center;
  background-color: white;
  position: relative;
}

.error-icon {
  width: 64px;
  height: 64px;
  background-color: #ffe4e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 32px auto 24px;
}

.error-icon::before {
  content: "";
  width: 24px;
  height: 24px;
  background-color: transparent;
  border: 2px solid #c9372c;
  border-radius: 50%;
  position: absolute;
}

.error-icon::after {
  content: "!";
  color: #c9372c;
  font-size: 18px;
  font-weight: bold;
  position: relative;
}

.error-title {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 16px;
  color: #212121;
}

.error-description {
  font-size: 16px;
  color: #757575;
  margin-bottom: 32px;
  line-height: 1.5;
}

.button-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px;
  background-color: white;
}

.error-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
}

.try-button {
  background-color: $pj-color-green;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: darken($pj-color-green, 10%);
  }
}

.device-id {
  font-size: 14px;
  color: #9e9e9e;
  margin-top: 32px;
}
</style>
